import { useState } from "react";
import useLang from "../../../hooks/useLang";
import { useParams } from "react-router-dom";
import useGetSpectrum from "./useGetSpectrum";
import {
  useDeleteSpectrumMutation,
  usePatchSpectrumNotesMutation,
  usePatchSpectrumNameMutation,
} from "../spectraSlice";
import useConfirmationModal from "../../forms/hooks/useConfirmationModal";
import useHasAccess from "../../../hooks/useHasAccess";
import { useSpectrumBlockPatchable } from "./useSpectrumBlock";
import usePatchSpectrum from "./usePatchSpectrum";
import useSpectrumFeaturesByLabelBlock from "./useSpectrumFeaturesByLabelBlock";
import useSpectrumTagsBlock from "./useSpectrumTagsBlock";
import useNotesBlock from "./useNotesBlock";
import useNameBlock from "./useNameBlock";
import usePatchSpectrumTags from "./usePatchSpectrumTags";
import useModifierModal from "../../forms/hooks/useModifierModal";

const useSpectrumPage = () => {
  const hasAccess = useHasAccess({ apiCall: "spectra.id.GET" });
  const lang = useLang();
  const [activeTab, setActiveTab] = useState("FEATURES");
  const [editingFeatures, setEditingFeatures] = useState(false);
  const { spectrumId } = useParams();
  const spectrum = useGetSpectrum(
    { id: spectrumId },
    { skip: spectrumId == null || !hasAccess }
  );

  const { formActions: deleteFormActions } = useConfirmationModal({
    mutationHook: useDeleteSpectrumMutation,
    params: { id: spectrumId },
    navigateOnSuccess: () => "/spectra/",
  });

  const { formAttribs: editFormAttribs, formActions: editSpectrum } =
    useModifierModal({
      mutationHook: usePatchSpectrum,
      formConfigHook: useSpectrumBlockPatchable,
      params: { id: spectrumId },
    });

  const featuresByLabelItems = useSpectrumFeaturesByLabelBlock({
    editing: editingFeatures,
  });
  const tagsBlockItems = useSpectrumTagsBlock();
  const { formAttribs: tagsFromAttribs, formActions: editTags } =
    useModifierModal({
      mutationHook: usePatchSpectrumTags,
      formConfigHook: useSpectrumTagsBlock,
    });
  const notesBlockItems = useNotesBlock();
  const { formAttribs: notesFromAttribs, formActions: editNotes } =
    useModifierModal({
      mutationHook: usePatchSpectrumNotesMutation,
      params: { id: spectrumId },
      formConfigHook: useNotesBlock,
    });
  const { formAttribs: nameFromAttribs, formActions: editName } =
    useModifierModal({
      mutationHook: usePatchSpectrumNameMutation,
      params: { id: spectrumId },
      formConfigHook: useNameBlock,
    });

  const participantSection = spectrum?.data?.participant
    ? {
        direction: "row",
        items: [
          {
            id: "iid",
            value: spectrum?.data?.participant?.iid,
          },
          {
            id: "date_of_birth",
            value: spectrum?.data?.participant?.date_of_birth,
          },
          {
            id: "gender",
            value: spectrum?.data?.participant?.gender,
          },
        ],
      }
    : {};

  const page = {
    hasAccess,
    header: {
      title:
        spectrum?.data?.name ||
        spectrum?.data?.id ||
        lang("spectra.example_loading"),
      lastEdit: spectrum?.data?.last_edit,
      buttons: [
        {
          caption: lang("captions.rename"),
          title: lang("spectra.edit_name"),
          type: "modifier-modal",
          mutation: "spectra.id.name.PATCH",
          formAttribs: nameFromAttribs,
          ...editName,
        },
        {
          caption: lang("common.delete"),
          type: "confirmation-modal",
          mutation: "spectra.id.DELETE",
          deemphasize: true,
          confirmationText: `${lang("common.delete")} ${spectrum?.data?.id}?`,
          ...deleteFormActions,
        },
      ],
    },
    articles: [
      {
        header: {
          buttons: {
            caption: lang("common.edit"),
            icon: "pencil",
            title: lang("common.edit"),
            type: "modifier-modal",
            mutation: "spectra.id.PATCH",
            formAttribs: editFormAttribs,
            ...editSpectrum,
          },
        },
        sections: [
          {
            items: [
              {
                id: "spectrum",
                type: "spectrum",
                data: spectrum?.data,
              },
            ],
          },
          { ...participantSection },
        ],
      },
      {
        header: {
          title: lang("captions.notes"),
          buttons: {
            caption: lang("common.edit"),
            icon: "pencil",
            title: lang("spectra.edit_notes"),
            type: "modifier-modal",
            mutation: "spectra.id.notes.PATCH",
            formAttribs: notesFromAttribs,
            ...editNotes,
          },
        },
        items: notesBlockItems,
      },
      {
        tabs: {
          TAGS: {
            id: "TAGS",
            label: lang("tags.title"),
            active: activeTab === "TAGS",
            onClick: () => {
              setActiveTab("TAGS");
            },
          },
          FEATURES: {
            id: "FEATURES",
            label: lang("features.title"),
            active: activeTab === "FEATURES",
            onClick: () => {
              setActiveTab("FEATURES");
            },
          },
        },
        sections: [
          {
            header: {
              title: lang("tags.title"),
              buttons: {
                caption: lang("common.edit"),
                icon: "pencil",
                title: lang("spectra.edit_tags_list"),
                type: "modifier-modal",
                mutation: "spectra.id.tags.POST",
                formAttribs: tagsFromAttribs,
                ...editTags,
              },
            },
            tabId: "TAGS",
            items: tagsBlockItems,
          },
          {
            header: {
              title: lang("features.title"),
              buttons: {
                caption: editingFeatures
                  ? lang("features.stop_editing")
                  : lang("features.edit_labels"),
                icon: "pencil",
                mutation: "features.id.examples.PATCH",
                onClick: () => setEditingFeatures((v) => !v),
              },
            },
            tabId: "FEATURES",
            items: featuresByLabelItems,
          },
        ],
      },
    ],
  };

  return page;
};

export default useSpectrumPage;
